import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe, Check } from 'lucide-react';
import { useCurrencyStore, AVAILABLE_CURRENCIES } from '../../stores/currencyStore';

interface CurrencySelectorProps {
  variant?: 'header' | 'footer' | 'dropdown';
}

export function CurrencySelector({ variant = 'header' }: CurrencySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currency, setCurrency, detectedCurrency, isManuallySet, resetToDetected } = useCurrencyStore();

  const currentCurrency = AVAILABLE_CURRENCIES.find((c) => c.code === currency);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCurrencies = AVAILABLE_CURRENCIES.filter(
    (c) =>
      c.code.toLowerCase().includes(search.toLowerCase()) ||
      c.name.toLowerCase().includes(search.toLowerCase())
  );

  // Popular currencies shown at the top
  const popularCodes = ['USD', 'EUR', 'GBP', 'IDR', 'MYR', 'SGD', 'AUD', 'JPY'];
  const popularCurrencies = popularCodes
    .map((code) => AVAILABLE_CURRENCIES.find((c) => c.code === code))
    .filter(Boolean) as typeof AVAILABLE_CURRENCIES;

  const handleSelect = (code: string) => {
    setCurrency(code);
    setIsOpen(false);
    setSearch('');
  };

  if (variant === 'footer') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 text-text-secondary hover:text-cream transition-colors text-sm"
        >
          <Globe className="w-4 h-4" />
          <span>{currentCurrency?.symbol} {currency}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute bottom-full mb-2 left-0 w-64 bg-surface border border-gold/20 rounded-lg shadow-xl z-50">
            <CurrencyDropdownContent
              search={search}
              setSearch={setSearch}
              filteredCurrencies={filteredCurrencies}
              popularCurrencies={popularCurrencies}
              currency={currency}
              detectedCurrency={detectedCurrency}
              isManuallySet={isManuallySet}
              handleSelect={handleSelect}
              resetToDetected={resetToDetected}
            />
          </div>
        )}
      </div>
    );
  }

  // Default header variant
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-leather border border-gold/20 rounded-lg text-cream hover:border-gold/40 transition-colors text-sm"
        title="Change currency"
      >
        <span className="font-medium">{currentCurrency?.symbol}</span>
        <span className="text-text-secondary">{currency}</span>
        <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 w-72 bg-surface border border-gold/20 rounded-lg shadow-xl z-50">
          <CurrencyDropdownContent
            search={search}
            setSearch={setSearch}
            filteredCurrencies={filteredCurrencies}
            popularCurrencies={popularCurrencies}
            currency={currency}
            detectedCurrency={detectedCurrency}
            isManuallySet={isManuallySet}
            handleSelect={handleSelect}
            resetToDetected={resetToDetected}
          />
        </div>
      )}
    </div>
  );
}

// Shared dropdown content
function CurrencyDropdownContent({
  search,
  setSearch,
  filteredCurrencies,
  popularCurrencies,
  currency,
  detectedCurrency,
  isManuallySet,
  handleSelect,
  resetToDetected,
}: {
  search: string;
  setSearch: (s: string) => void;
  filteredCurrencies: typeof AVAILABLE_CURRENCIES;
  popularCurrencies: typeof AVAILABLE_CURRENCIES;
  currency: string;
  detectedCurrency: string;
  isManuallySet: boolean;
  handleSelect: (code: string) => void;
  resetToDetected: () => void;
}) {
  return (
    <>
      {/* Search */}
      <div className="p-3 border-b border-gold/10">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search currencies..."
          className="w-full px-3 py-2 bg-leather border border-gold/20 rounded-lg text-cream placeholder-text-muted focus:border-gold focus:outline-none text-sm"
          autoFocus
        />
      </div>

      {/* Reset to detected */}
      {isManuallySet && (
        <div className="px-3 py-2 border-b border-gold/10">
          <button
            onClick={() => {
              resetToDetected();
            }}
            className="w-full text-left text-sm text-gold hover:text-gold-bright transition-colors flex items-center gap-2"
          >
            <Globe className="w-4 h-4" />
            Reset to detected ({detectedCurrency})
          </button>
        </div>
      )}

      {/* Currency list */}
      <div className="max-h-64 overflow-y-auto">
        {/* Popular currencies (when not searching) */}
        {!search && (
          <>
            <div className="px-3 py-2 text-xs text-text-muted uppercase tracking-wide">
              Popular
            </div>
            {popularCurrencies.map((c) => (
              <CurrencyOption
                key={c.code}
                currency={c}
                isSelected={currency === c.code}
                onSelect={handleSelect}
              />
            ))}
            <div className="px-3 py-2 text-xs text-text-muted uppercase tracking-wide border-t border-gold/10 mt-1">
              All Currencies
            </div>
          </>
        )}

        {/* Filtered/All currencies */}
        {filteredCurrencies.length === 0 ? (
          <div className="px-3 py-4 text-center text-text-muted text-sm">
            No currencies found
          </div>
        ) : (
          filteredCurrencies.map((c) => (
            <CurrencyOption
              key={c.code}
              currency={c}
              isSelected={currency === c.code}
              onSelect={handleSelect}
            />
          ))
        )}
      </div>

      {/* Footer note */}
      <div className="px-3 py-2 border-t border-gold/10 text-xs text-text-muted">
        Prices shown are estimates. Checkout is in USD.
      </div>
    </>
  );
}

function CurrencyOption({
  currency,
  isSelected,
  onSelect,
}: {
  currency: (typeof AVAILABLE_CURRENCIES)[0];
  isSelected: boolean;
  onSelect: (code: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(currency.code)}
      className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-leather/50 transition-colors ${
        isSelected ? 'bg-gold/10' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <span className="w-8 text-center font-medium text-gold">{currency.symbol}</span>
        <div>
          <div className="text-sm text-cream">{currency.code}</div>
          <div className="text-xs text-text-muted">{currency.name}</div>
        </div>
      </div>
      {isSelected && <Check className="w-4 h-4 text-gold" />}
    </button>
  );
}
